/* eslint-disable @typescript-eslint/no-shadow */
import React, { useEffect, useState, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Separator,
} from '@lb-classroom/ui';
import { useRouter } from 'next/router';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { i18n, withTranslation } from 'next-i18next';
import { TFunction } from 'i18next';
import classnames from 'classnames';
import { hasRole } from '@lb-classroom/feature-auth';
import * as AuthActions from '../../../actions/auth';
import * as ModalActions from '../../../actions/modal';
import * as FlashActions from '../../../actions/flash';
import TextInput from '../../UI/atoms/form/TextInput';
import UserValidator from '../../../validators/UserValidator';
import appUtils, { isMobile } from '../../../utils/appUtils';
import { GoogleOAuthButton } from '../google-oauth-button';
import { FEATURE_FLAGS } from '../../../constants/feature-flags';

interface User {
  profile_type: string;
  locale: {
    i18n_form: string;
  };
}

interface GeoData {
  countryCode: string;
  ip: string;
}

interface RootState {
  auth: {
    user: User | null;
    waiting: boolean;
  };
  geoData: {
    geoData: GeoData;
  };
}

interface StateProps {
  user: User | null;
  waiting: boolean;
  geoData: GeoData;
}

interface DispatchProps {
  authActions: typeof AuthActions;
  modalActions: typeof ModalActions;
  flashActions: typeof FlashActions;
}

interface OwnProps {
  t: TFunction;
  inModal?: boolean;
  clearModalMessage?: () => void;
}

type LoginProps = StateProps & DispatchProps & OwnProps;

function mapStateToProps(state: RootState): StateProps {
  const { auth, geoData } = state;
  const { user, waiting } = auth;
  return {
    user,
    waiting,
    geoData: geoData.geoData,
  };
}

function mapDispatchToProps(dispatch: Dispatch): DispatchProps {
  return {
    authActions: bindActionCreators<any, any>(AuthActions, dispatch),
    modalActions: bindActionCreators<any, any>(ModalActions, dispatch),
    flashActions: bindActionCreators<any, any>(FlashActions, dispatch),
  };
}

interface ValidationResult {
  success: boolean;
  message?: string;
}

interface FormState {
  email: string;
  password: string;
  emailValidationResult: ValidationResult | null;
  passwordValidationResult: ValidationResult | null;
}

const Login: React.FC<LoginProps> = (props) => {
  const router = useRouter();
  const language = i18n.language;
  const currentLocale = appUtils.getLocaleFromLang(language);
  const {
    t,
    inModal = false,
    authActions,
    modalActions,
    flashActions,
    geoData,
    user,
    waiting,
  } = props;
  const [state, setState] = useState<FormState>({
    email: '',
    password: '',
    emailValidationResult: null,
    passwordValidationResult: null,
  });
  const { email, password, emailValidationResult, passwordValidationResult } =
    state;

  useEffect(() => {
    if (!props.inModal) {
      flashActions.clearMessage();
    }
  }, [props.inModal, flashActions]);

  // Track if redirect has happened to prevent duplicate redirects
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (hasRedirected.current) return;

    if (user) {
      hasRedirected.current = true;
      const isTutor = hasRole(user.profile_type, 'Tutor');
      const isStudent = hasRole(user.profile_type, 'Student');

      // Tutors (including admins with both roles) go to tutor dashboard
      if (isTutor) {
        router.push('/t/dashboard');
      } else if (isStudent) {
        // Students go to student dashboard with locale
        i18n.changeLanguage(user.locale.i18n_form);
        router.push('/s/dashboard', '/s/dashboard', {
          locale: user.locale.i18n_form,
        });
      } else {
        // Unknown role, redirect to home
        router.push('/');
      }
    }
  }, [user, router]);

  const hideModalPromise = () => {
    return new Promise<string>((resolve, reject) => {
      modalActions.hide();
      window.setTimeout(() => {
        resolve('hide');
      }, 300);
    });
  };

  const transitToSignup = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (inModal) {
      await hideModalPromise();
      modalActions.show('signup');
    } else {
      router.push('/signup');
    }
  };

  const showForgotPasswordModal = async () => {
    if (inModal) {
      await hideModalPromise();
      modalActions.show('forgotPassword');
    } else {
      modalActions.show('forgotPassword');
    }
  };

  const onUpdateEmail = (value: string) => {
    setState({
      ...state,
      email: value,
    });
  };

  const onUpdatePassword = (value: string) => {
    setState({
      ...state,
      password: value,
    });
  };

  const isValid = (results: ValidationResult[]) => {
    // Always update state using functional updater to avoid stale state issues
    setState((prev) => ({
      ...prev,
      emailValidationResult: results[0],
      passwordValidationResult: results[1],
    }));
    return results[0].success && results[1].success;
  };

  const onSubmit = async (event: React.FormEvent | React.KeyboardEvent) => {
    event.preventDefault();
    modalActions.clearMessage();
    flashActions.clearMessage();
    const { email, password } = state;
    const emailValidationResult = await UserValidator.emailValidator({
      name: t('form.email'),
      value: email,
      required: true,
      t,
    });
    const passwordValidationResult = await UserValidator.passwordValidator({
      name: t('form.password'),
      value: password,
      required: true,
      t,
    });
    if (isValid([emailValidationResult, passwordValidationResult])) {
      authActions.login(
        email,
        password,
        geoData.countryCode,
        geoData.ip,
        currentLocale,
        inModal,
      );
    }
  };

  const emailProps = {
    placeholder: t('form.email'),
    className: 'email',
    value: email,
    name: 'email',
    type: 'email',
    updateHandler: onUpdateEmail,
    validationResult: emailValidationResult,
    invalidLabel: true,
    onKeyDown: onSubmit,
  };
  const passwordProps = {
    placeholder: t('form.password'),
    className: 'password',
    value: password,
    name: 'password',
    type: 'password',
    updateHandler: onUpdatePassword,
    validationResult: passwordValidationResult,
    invalidLabel: true,
    onKeyDown: onSubmit,
  };
  const panelBodyClass = classnames({ loading: waiting });
  let panelHeader = isMobile() ? (
    <div>
      <span>{t('modals.login.login')}</span>
    </div>
  ) : null;

  if (inModal) {
    panelHeader = null;
  }

  return (
    <div className="max-w-md mx-auto px-0 sm:px-2">
      <Card>
        {panelHeader && (
          <CardHeader className="bg-slate-200">
            <CardTitle>{panelHeader}</CardTitle>
          </CardHeader>
        )}
        <CardContent>
          <div className={panelBodyClass}>
            {FEATURE_FLAGS.googleOAuth && (
              <>
                <div className="mb-4">
                  <GoogleOAuthButton context="signin" />
                </div>
                <div className="flex items-center gap-3 my-4">
                  <Separator className="flex-1" />
                  <span className="text-sm text-slate-500">
                    {t('auth.orSeparator')}
                  </span>
                  <Separator className="flex-1" />
                </div>
              </>
            )}
            <form
              method="post"
              className="login-form"
              onSubmit={onSubmit}
              noValidate
            >
              <div className="control-group mb-4">
                <TextInput {...emailProps} />
              </div>
              <div className="control-group mb-4">
                <TextInput {...passwordProps} />
              </div>
              <div className="clear-both mb-2">
                <a
                  className="text-link underline cursor-pointer hover:text-link-active"
                  onClick={showForgotPasswordModal}
                >
                  {t('modals.login.forgotPassword')}
                </a>
              </div>
              <div className="control-group">
                <Button
                  onClick={onSubmit}
                  className="w-full"
                  size="lg"
                  type="submit"
                  disabled={waiting}
                >
                  {t('modals.login.login')}
                </Button>
              </div>
            </form>
          </div>
          <Separator className="my-4" />
          <div>
            <span>
              {t('modals.login.noAccount')}{' '}
              <a
                onClick={transitToSignup}
                className="text-link underline cursor-pointer hover:text-link-active"
              >
                {t('modals.login.signup')}
              </a>
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const wrapped = withTranslation('common')(Login);
export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
